import React, { useState, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { exists, noop, useSessionCtx, getDebugFn, redirectWindowLocation, Analytics } from "utils";
import { getIP } from "api/accounts-customer";
import { getGeoData } from "api/accounts";
import { finishCreatePaypal } from "api/payments";
import { FLOW_STATE } from "components/Paypal";
import { FullPageSpinner } from "components/Spinner";
import { getFlowDestination } from "containers/App/AppAuth";
import * as accountsCustomerApi from "api/accounts-customer";
import { appendStateToRedirectUrl, getPaypalToken, getRedirectState } from "./common";
import { isRedirectURLAllowed, RedirectNotAllowedError } from "utils/redirect";

const finishCreate = async (sessionJWT, paypalToken, debug = noop) => {
  try {
    const ipAddress = await getIP();
    const geoData = await getGeoData(sessionJWT, ipAddress.ip);
    const geoIPState = geoData.payload.region;
    const geoIPCountry = geoData.payload.country_code;
    const finishResult = await finishCreatePaypal(
      sessionJWT,
      paypalToken,
      geoIPState,
      geoIPCountry
    );
    debug("PaypalSuccess__finishCreate__result: ", JSON.stringify(finishResult));
    return { status: FLOW_STATE.COMPLETE, id: finishResult.payload.id };
  } catch (error) {
    accountsCustomerApi.logError(null, { paypalToken, error });
    debug("PaypalSuccess__finishCreate__error: ", error);
    return { status: FLOW_STATE.ERROR, id: null };
  }
};

export const PaypalSuccess = () => {
  const analyticsInstance = Analytics();
  const location = useLocation();
  const history = useHistory();
  const { setFlowState } = useSessionCtx();
  const sessionPayload = useRef(null); // hold on to session payload after loading so we can use to assign logs to the user

  const redirectToken = getRedirectState(location);
  const paypalToken = getPaypalToken(location);

  const [finishCreateResult, setFinishCreateResult] = useState(null);
  useEffect(() => {
    const logFields = {};
    const processPaypalSuccess = async () => {
      try {
        logFields.paypalToken = paypalToken;

        logFields.location = "verifyToken";
        const verifyResponse = await accountsCustomerApi.verifyToken(redirectToken);
        const session = verifyResponse.data;
        sessionPayload.current = session; // hold session so we can use with logging later
        const debug = getDebugFn(session?.sessionCtx);

        logFields.location = "finishCreate";
        const finishCreateResponse = await finishCreate(session.sessionJWT, paypalToken);
        debug("finishCreateResponse", finishCreateResponse);
        setFinishCreateResult(finishCreateResponse);

        const { status, id } = finishCreateResponse;
        const state = `${status}${id ? `.${id}` : ""}`;
        logFields.state = state;
        const flowConfig = session?.sessionCtx?.flowConfig || {};
        const postOAuthURLOverride = flowConfig.postOAuthURLOverride || flowConfig.connectPaypalCompleteUrlOverride; // TODO: may not have to support connectPaypalCompleteUrlOverride if applovin isn't using yet.
        const intCtx = session?.sessionCtx?.intCtx;
        debug("intCtx", intCtx);

        if (intCtx) {
          // widget / iframe
          /**
           * 'intCtx' is supplied through the widget script that generates the iFrame the widget url
           * is loaded into and captures the wrapping page's window.location.href.
           * If this value is set, we know we began from the widget script
           * within a publisher's web page.
           * If the publisher has supplied a flowConfig with a 'postOAuthURLOverride' value,
           * we will use this url instead of intCtx to redirect to when complete.
           * Currently the only time we expect postOAuthURLOverride to be used
           * is in the case the publisher's app is something like Capacitor JS / Cordova JS or something similar
           * where a native app (ios/android) presents a browser with web content loaded into it
           * that is the main UI of the application (running on something like capacitor://localhost).
           * Since the core code won't allow a redirect away from the app's content in the browser, it will
           * instead kick the user out to a separate browser application.
           * This allows a publisher to supply a universal link or custom URL scheme to direct
           * the user back into the originating application after paypal completion.
           * It is expected that the URL supplied by the publisher will re-open the original flow
           * (which will now include the added paypal account, if added by the user).
           * @todo There's a lot of repeated code here in /src/containers/Pages/PaypalCallback/PaypalCancel
           * that could likely get refactored.
          */
          const finalUrl = appendStateToRedirectUrl((postOAuthURLOverride || intCtx), state);
          debug("finalUrl", finalUrl);

          // Validate redirect URL against the integrator's allowed patterns, which
          // were signed into the redirect token at flow start (SettingsProvider is
          // not populated on this callback route, but the verified token payload is
          // trustworthy because it is server-signed).
          const redirectAllowed = isRedirectURLAllowed(finalUrl, {
            allowed_domain_redirect_patterns: session?.allowedDomainRedirectPatterns,
          });
          if (!redirectAllowed) {
            const error = new RedirectNotAllowedError();
            logFields.redirectUrl = finalUrl;
            logFields.error = error.message;
            analyticsInstance.track({
              title: "[ANALYTICS]: TRACK",
              eventStr: "PayPal_Redirect_Blocked",
              payload: {
                redirectUrl: finalUrl,
                flow: session?.sessionCtx?.flow,
              },
              jwt: sessionPayload.current?.sessionJWT,
            });
            throw error;
          }

          analyticsInstance.track({
            title: "[ANALYTICS]: TRACK",
            eventStr: "PayPal_Process_OAuth_Success_Return",
            payload: {
              isSuccess: status.startsWith(FLOW_STATE.COMPLETE),
              isIFrame: true,
              redirectUrl: finalUrl,
              flow: session?.sessionCtx?.flow,
            },
            jwt: sessionPayload.current?.sessionJWT,
          });
          await new Promise((resolve) => setTimeout(resolve, 100)); // allow analytics to flush
          redirectWindowLocation(finalUrl);
        } else {
          // fullpage
          setFlowState(state);
          const flow = session?.sessionCtx?.flow;
          const flowDestination = getFlowDestination(flow);
          analyticsInstance.track({
            title: "[ANALYTICS]: TRACK",
            eventStr: "PayPal_Process_OAuth_Success_Return",
            payload: {
              isSuccess: true,
              isIFrame: false,
              redirectUrl: flowDestination,
              flow,
            },
            jwt: sessionPayload.current?.sessionJWT,
          });
          await new Promise((resolve) => setTimeout(resolve, 100)); // allow analytics to flush
          history.replace(flowDestination);
        }
      } catch (error) {
        analyticsInstance.track({
          title: "[ANALYTICS]: TRACK",
          eventStr: "PayPal_Process_OAuth_Success_Return",
          payload: {
            isSuccess: false,
            flow: sessionPayload.current?.sessionCtx?.flow || null, // if token validation fails we won't have this payload and value will be null
            error: error?.message || "unable to parse error message",
          },
          jwt: sessionPayload.current?.sessionJWT || null, // if token validation fails we won't have this payload and value will be null
        });
        accountsCustomerApi.logError(sessionPayload.current?.sessionJWT || null, logFields);

        // push error up the global application boundary
        setFinishCreateResult(() => {
          throw error;
        });
      }
    };
    if (exists(redirectToken) && exists(paypalToken)) {
      processPaypalSuccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectToken, paypalToken, history, setFlowState]);

  if (finishCreateResult) {
    return <FullPageSpinner data-testid="paypal-success-redirect" />;
  }
  return <FullPageSpinner data-testid="paypal-success" />;
};
